import {
  useEffect,
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import { useParams, useSearchParams } from "react-router";
import PropTypes from "prop-types";
import {
  CardView,
  SkeletonCollection,
  ActionBar,
  ActionButton,
  Text,
  DialogTrigger,
  DialogContainer,
  AlertDialog,
  TextField,
  mergeStyles,
} from "@react-spectrum/s2";
import { Collection } from "react-aria-components";
import Delete from "@react-spectrum/s2/icons/Delete";
import Rename from "@react-spectrum/s2/icons/Rename";
import Share from "@react-spectrum/s2/icons/Share";
import Copy from "@react-spectrum/s2/icons/Copy";

import { ProjectsListCard } from "./ProjectsListCard";
import { ShareVideoDialog } from "./ShareVideoDialog";

import {
  useVideos,
  useDeleteVideo,
  useUpdateVideo,
  useDuplicateVideo,
} from "@app/hooks/videos";
import { store } from "../../store";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const ProjectsList = ({
  limit = -1,
  isQuiet = false,
  selectionMode = "multiple",
  renderEmptyState = () => null,
  onCardPress,
  styles = "",
}) => {
  const params = useParams();
  const [searchParams] = useSearchParams();

  const isEmptySearchParam = searchParams.get("empty");
  const isEmpty = ["", "true"].includes(isEmptySearchParam) ?? isQuiet ?? false;

  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [shareVideoId, setShareVideoId] = useState(null);

  const videos = useVideos({
    enabled: !isEmpty,
    user: params?.email ?? "me",
    pageSize: limit !== -1 && typeof limit === "number" ? limit : undefined,
  });
  const updateVideo = useUpdateVideo();
  const deleteVideo = useDeleteVideo();
  const duplicateVideo = useDuplicateVideo();

  const loadingState = videos.isError
    ? "error"
    : videos.isPending || videos.isLoading
      ? "loading"
      : videos.isFetchingNextPage
        ? "loadingMore"
        : "idle";

  const [currentLimit, setCurrentLimit] = useState(
    typeof limit === "number" ? limit : -1,
  );
  const listRef = useRef(null);

  const handleLoadMore = useCallback(() => {
    if (
      !isEmpty &&
      videos.hasNextPage &&
      !videos.isFetching &&
      (limit !== "row" || currentLimit > 0) &&
      (currentLimit <= 0 || (videos.data?.length ?? 0) < currentLimit)
    ) {
      videos.fetchNextPage({ cancelRefetch: false });
    }
  }, [
    isEmpty,
    videos.hasNextPage,
    videos.isFetching,
    videos.data?.length,
    videos.fetchNextPage,
    currentLimit,
    limit,
  ]);

  useEffect(() => {
    if (typeof limit === "number" && limit !== currentLimit) {
      setCurrentLimit(limit);
    }
  }, [limit, currentLimit]);

  const handleResize = useCallback(() => {
    const listEl = listRef.current?.UNSAFE_getDOMNode();

    if (!listEl) return;

    const gridEl = listEl.querySelector("[role='grid']") ?? listEl;
    const itemEl = gridEl?.firstElementChild?.firstElementChild;

    const itemWidth = (itemEl?.offsetWidth ?? 165) + 6;
    const listWidth = listEl.offsetWidth;

    const possibleItems = Math.max(1, Math.floor(listWidth / itemWidth));

    if (possibleItems !== limit) {
      setCurrentLimit(possibleItems);
    }
  }, [limit]);

  useLayoutEffect(() => {
    if (limit !== "row") {
      return;
    }

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [videos.data, handleResize, limit]);

  const items = useMemo(() => {
    if (isEmpty) {
      return [];
    }

    return (
      videos.data
        ?.slice(0, currentLimit > 0 ? currentLimit : undefined)
        ?.filter((item) => !!item?.id) ?? []
    );
  }, [videos.data, currentLimit, isEmpty]);

  const shouldShowSkeleton = useMemo(() => {
    return (
      !isEmpty &&
      (videos.isPending || videos.isFetchingNextPage) &&
      (currentLimit <= 0 || items.length < currentLimit)
    );
  }, [
    videos.isPending,
    videos.isFetchingNextPage,
    isEmpty,
    items.length,
    currentLimit,
  ]);

  const handleVideosDelete = useCallback(
    (videoIds) => {
      setSelectedKeys(new Set());
      [...videoIds].forEach((id) => {
        deleteVideo.mutate(
          { id },
          {
            onSuccess: () => {
              store
                .getState()
                .addToast(
                  `Video${videoIds.size > 1 ? "s" : ""} deleted successfully`,
                  {
                    variant: "positive",
                    timeout: 3000,
                  },
                );
            },
          },
        );
      });
    },
    [deleteVideo.mutate],
  );

  const [tempName, setTempName] = useState("");
  const handleVideoDuplicate = (id) => {
    duplicateVideo.mutate(
      { id },
      {
        onSuccess: () => {
          setSelectedKeys(new Set());
          store.getState().addToast("Project duplicated successfully", {
            variant: "positive",
            timeout: 3000,
          });
        },
      },
    );
  };

  const handleVideoRename = useCallback(
    (id, newName) => {
      updateVideo.mutate({ id, name: newName });
    },
    [updateVideo.mutate],
  );

  const handleVideoShare = async (id, emails) => {
    try {
      const video = videos.data?.find((item) => item.id === id);
      if (!video) throw new Error("Project not found");

      const current = video.sharedWith ?? [];
      const currentLower = new Set(current.map((item) => item.toLowerCase()));
      const added = emails.filter((item) => !currentLower.has(item));

      if (added.length === 0) {
        store
          .getState()
          .addToast("Project is already shared with these users", {
            variant: "info",
            timeout: 3000,
          });
        return;
      }

      await updateVideo.mutateAsync({
        id,
        sharedWith: [...current, ...added],
      });

      store.getState().addToast(`Project shared with ${added.join(", ")}`, {
        variant: "positive",
        timeout: 3000,
      });
    } catch (error) {
      store.getState().addToast("Unable to share project. Please try again.", {
        variant: "negative",
        timeout: 5000,
      });
      throw error;
    }
  };

  return (
    <>
      <CardView
        ref={listRef}
        loadingState={loadingState}
        onLoadMore={handleLoadMore}
        selectionMode={selectionMode}
        selectionStyle={selectionMode === "multiple" ? "checkbox" : "highlight"}
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        renderEmptyState={renderEmptyState}
        renderActionBar={
          selectionMode === "multiple"
            ? (selectedKeys) => (
                <ActionBar isEmphasized>
                  {selectedKeys.size === 1 && (
                    <DialogTrigger>
                      <ActionButton>
                        <Share />
                        <Text>Share</Text>
                      </ActionButton>
                      <ShareVideoDialog
                        videoId={[...selectedKeys][0]}
                        onAccept={(email) =>
                          handleVideoShare([...selectedKeys][0], email)
                        }
                      />
                    </DialogTrigger>
                  )}
                  {selectedKeys.size === 1 && (
                    <ActionButton
                      isDisabled={duplicateVideo.isPending}
                      onPress={() => handleVideoDuplicate([...selectedKeys][0])}
                    >
                      <Copy />
                      <Text>
                        {duplicateVideo.isPending
                          ? "Duplicating..."
                          : "Duplicate"}
                      </Text>
                    </ActionButton>
                  )}
                  {selectedKeys.size === 1 && (
                    <DialogTrigger>
                      <ActionButton>
                        <Rename />
                        <Text>Rename</Text>
                      </ActionButton>
                      <AlertDialog
                        title="Rename Project"
                        primaryActionLabel="Rename"
                        cancelLabel="Cancel"
                        onPrimaryAction={() => {
                          handleVideoRename([...selectedKeys][0], tempName);
                          setTempName("");
                        }}
                      >
                        {/* Dialog content for renaming */}
                        <TextField
                          label="Title"
                          defaultValue={
                            videos.data.find(
                              (video) => video.id === [...selectedKeys][0],
                            )?.name || ""
                          }
                          onChange={setTempName}
                        />
                      </AlertDialog>
                    </DialogTrigger>
                  )}
                  <ActionButton
                    onPress={() => handleVideosDelete(selectedKeys)}
                  >
                    <Delete />
                    <Text>Delete</Text>
                  </ActionButton>
                </ActionBar>
              )
            : null
        }
        size="S"
        aria-label="Projects list"
        styles={mergeStyles(
          style({
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 0,
            minHeight: 0,
            boxSizing: "border-box",
          }),
          styles,
        )}
      >
        <Collection
          items={items}
          dependencies={
            process.env.NODE_ENV === "development" ? [+new Date()] : []
          }
        >
          {(item) => (
            <ProjectsListCard
              item={item}
              onPress={() => onCardPress(item)}
              isDuplicating={duplicateVideo.isPending}
              onMenuAction={(action, video) => {
                if (action === "share") {
                  setShareVideoId(video.id);
                } else if (action === "duplicate") {
                  handleVideoDuplicate(video.id);
                }
              }}
            />
          )}
        </Collection>
        {shouldShowSkeleton && (
          <SkeletonCollection>
            {() => (
              <ProjectsListCard
                item={{
                  id: "",
                  name: "Loading…",
                  rendition_grid: {
                    main: {},
                    tiles: [{ name: 1 }, { name: 2 }, { name: 3 }],
                  },
                }}
              />
            )}
          </SkeletonCollection>
        )}
      </CardView>
      <DialogContainer onDismiss={() => setShareVideoId(null)}>
        {shareVideoId && (
          <ShareVideoDialog
            videoId={shareVideoId}
            onAccept={(emails) => handleVideoShare(shareVideoId, emails)}
          />
        )}
      </DialogContainer>
    </>
  );
};

ProjectsList.propTypes = {
  ref: PropTypes.object,
  limit: PropTypes.number,
  isQuiet: PropTypes.bool,
  selectionMode: PropTypes.oneOf(["none", "single", "multiple"]),
  renderEmptyState: PropTypes.func,
  onCardPress: PropTypes.func,
  styles: PropTypes.object,
};
